import { useDriveOnContext } from '@/contexts/DriveOnContext';
import {
  createDriveOnReport,
  getDriveOnReportResult,
  getDriveOnReports,
  getLatestDriveOnDriverInput,
} from '@/generated/client/driveon-api/driveon-api';
import { DriveOnReportDto, DriveOnStatusDto, DriveOnReportDtoStatus as ReportStatus } from '@/generated/model';
import { FileDownloadOffOutlined, FileDownloadOutlined } from '@mui/icons-material';
import { Button, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotificationContext } from '@/contexts/NotificationCtx';
import { useNavigate } from 'react-router';
import { DriveOnDriverInputInputType as InputType } from '@/generated/model/driveOnDriverInputInputType';
import { Logbook } from '@/model/Logbook';

import './ReportTable.css';

export function ReportTable() {
  const driveOnCtx = useDriveOnContext();
  const notifyCtx = useNotificationContext();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [reports, setReports] = useState<DriveOnReportDto[] | undefined>(undefined);

  useEffect(() => {
    const contextIsNotLoaded = !driveOnCtx.inputType && !driveOnCtx.logbook && !driveOnCtx.target && !driveOnCtx.task;
    async function reloadPageContent() {
      if (contextIsNotLoaded) {
        const result = await getLatestDriveOnDriverInput().catch((error) => {
          const data = error.response.data;
          if (data.exceptionClass.includes('UserNotInDriverInputException')) {
            notifyCtx?.showNotification('error', t('report.missingTarget'), undefined, 10);
            navigate('/menetlevel/jarmuvalaszto');
          }
        });

        if (result && result.status == 200) {
          const data = result.data;
          await updateDriveOnContext(data);
          console.log('reloadPageContent(): Api called: getLatestDriveOnDriverInput');
        }
      }
    }

    async function updateDriveOnContext(data: DriveOnStatusDto) {
      const driverInput = data.driverInput;
      const inputType = driverInput?.inputType;
      driveOnCtx.setInputType(inputType);

      if (inputType === InputType.TARGET_UNASSIGNED) {
        eraseContext();
      } else if (inputType === InputType.MIDNIGHT_CLEANUP) {
        eraseContext();
      } else if (inputType === InputType.KICKED) {
        eraseContext();
      } else {
        await updateContext(data);
      }
    }

    function eraseContext() {
      driveOnCtx.setTarget(undefined);
      driveOnCtx.setTask(undefined);
      driveOnCtx.setLogbook(undefined);
      notifyCtx?.showNotification('error', t('report.missingTarget'), undefined, 5);
      navigate('/menetlevel/jarmuvalaszto');
    }

    async function updateContext(data: DriveOnStatusDto) {
      const driverInput = data.driverInput;
      const task = data.task;
      const target = data?.driverInput?.target;
      driveOnCtx.setTarget(target);
      driveOnCtx.setTask(task);
      driveOnCtx.setLogbook({
        taskName: driverInput?.task?.name,
        destination: driverInput?.destination,
        productDescription: driverInput?.productDescription,
        productWeight: driverInput?.productWeight,
        personCount: driverInput?.personCount,
        trailer: driverInput?.trailer,
        workingHours: driverInput?.target.egs ? 0 : driverInput?.workingHours,
        heatingHours: driverInput?.target.egs ? 0 : driverInput?.heatingHours,
        comment: driverInput?.comment,
      } satisfies Logbook);

      if (target) {
        const reportResult = await getDriveOnReports({ targetId: target.id }).catch((error) => {
          console.log(`Failed to request reports, error: ${error}`)
        });

        if (reportResult && reportResult.status == 200) {
          const data = reportResult.data;
          setReports(data);
          console.log('getReports(): Api called: getDriveOnReports');
        }  
      }
    }
  
    reloadPageContent();
  }, []);

  useEffect(() => {
    async function loadReports() {
      await getReports();
    }
    loadReports();
  }, []);

  async function generateReport() {
    const target = driveOnCtx.target;
    if (driveOnCtx.inputType && !target) {
      notifyCtx?.showNotification('error', t('report.missingTarget'), undefined, 5);
      navigate('/menetlevel/jarmuvalaszto');     
    }

    if (driveOnCtx.inputType && target) {
      await createDriveOnReport({ targetId: target.id, zonedDateTime: getUTCDateAtMidnight() }).catch((error) =>
        console.log(`Failed to create report, error: ${error}`)
      );    
    }
    await getReports();
  }

  async function downloadReport(reportId: string) {
    const result = await getDriveOnReportResult(reportId, {responseType: 'arraybuffer',}).catch((error) => {
      console.log(`Failed to request reports, error: ${error}`)
    });

    if (result && result.status == 200) {
      const data = result.data;
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const tempLink = document.createElement('a');
      tempLink.href = url;
      const fileName = `report_${driveOnCtx.target?.name}_${getUTCDateToday()}.xlsx`;
      tempLink.setAttribute('download', fileName);

      document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempLink);
      window.URL.revokeObjectURL(url);
      console.log('downloadReport(): Api called: getDriveOnReportResult');
    }
  }

  function showReportStatus(reportStatus: ReportStatus): string {
    switch (reportStatus) {
      case ReportStatus.SUCCESS:
        return 'report.status.success';
      case ReportStatus.FAILED:
        return 'report.status.failed';
      case ReportStatus.IN_PROGRESS:
        return 'report.status.inProgress';
    }
  }

  async function getReports() {
    const target = driveOnCtx.target;
    if (driveOnCtx.inputType && !target) {
      notifyCtx?.showNotification('error', t('report.missingTarget'), undefined, 5);
      navigate('/menetlevel/jarmuvalaszto');
    }
    if (driveOnCtx.inputType && target) {
      const result = await getDriveOnReports({ targetId: target.id }).catch((error) => {
        console.log(`Failed to request reports, error: ${error}`)
      });

      if (result && result.status == 200) {
        const data = result.data;
        setReports(data);
        console.log('getReports(): Api called: getDriveOnReports');
      }    
    }
  }

  function showDownloadButton(report: DriveOnReportDto) {
    const isSuccess = report.status == ReportStatus.SUCCESS;
    const icon = isSuccess ? <FileDownloadOutlined /> : <FileDownloadOffOutlined />;
    return (
      <div>
        <IconButton
          aria-label="download"
          disabled={!isSuccess}
          onClick={() => {
            downloadReport(report.reportId);
          }}
        >
          {icon}
        </IconButton>
      </div>
    );
  }

  function convertDateTime(timestamp: number): string {
    const timeOptions = { hour: '2-digit', minute: '2-digit' } satisfies Intl.DateTimeFormatOptions;
    return new Date(timestamp / 1000).toLocaleTimeString('hu-HU', timeOptions);
  }

  function getUTCDateToday() {
    const now = new Date();
    const year = now.getUTCFullYear();
    const month = String(now.getUTCMonth() + 1).padStart(2, '0');
    const day = String(now.getUTCDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  function getUTCDateAtMidnight() {
    return `${getUTCDateToday()}T00:00:00Z`;
  }

  return (
    <div className="report">
      <div className="title">{t('report.table.title')}</div>
      <div className="description">{t('report.table.description')}</div>
      <Stack className="header" padding={'16px'} justifyContent={'space-evenly'} direction={'row'}>
        <Button className="button" variant="contained" onClick={generateReport}>
          {t('report.table.button.new')}
        </Button>
        <Button className="button" variant="contained" onClick={getReports}>
          {t('report.table.button.refresh')}
        </Button>
      </Stack>
      <TableContainer className="table" component={Paper}>
        <Table aria-label="simple table">
          <TableBody>
            {reports?.map((report) => (
              <TableRow key={report.reportId}>
                <TableCell sortDirection="desc" align="left">
                  {convertDateTime(report.createdTimeMicros)}
                </TableCell>
                <TableCell align="center">{t(showReportStatus(report.status))}</TableCell>
                <TableCell align="right">{showDownloadButton(report)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
